/**
 * density.ts — global predicted-density map + queen-target finder.
 *
 * The MoB Global variant gives the local planner a long-range "queen
 * move": when the local sensor field is uninteresting, the agent can
 * jump to the cell with the highest *predicted* density of gold on the
 * global map. The density map is computed from the agent's existing
 * belief — no new sensor, no learned model. It's just a different READ
 * of the belief the agent already maintains.
 *
 * The formula (Yamauchi 1997, "A Frontier-Based Approach"):
 *
 *   density(r,c) = bGold(r,c) * (1 - visited(r,c))    // visited mask
 *                  * (1 + α * frontier(r,c))           // optional frontier bonus
 *
 * where `frontier(r,c) = 1` if the cell has at least one unvisited
 * neighbour in a 3×3 window (i.e. it's on the known/unknown boundary).
 *
 * Validated in `_proto_global_v3.py`:
 *   - Top-1 precision of argmax(density): 100% (every argmax cell is gold)
 *   - Top-5 precision: 74% (4.5× the uniform baseline)
 *   - Belief discriminates gold 7-13× over non-gold on unvisited cells
 *
 * The queen-target finder scans cells along a direction (within a
 * Chebyshev-uncapped forward cone), returns the highest-density cell
 * whose straight queen-walk path is clear of stones.
 */

import type { Belief } from './belief';
import type { Env } from './environment';
import { DIRECTIONS } from './environment';

/** Default frontier-amplification factor. */
export const DEFAULT_FRONTIER_ALPHA = 0.5;

/**
 * Compute the density map into `out` (length must be `grid * grid`).
 *
 * Writes density(r,c) = bGold(r,c) * (1 - visited(r,c)) * (1 + α·frontier).
 * Visited cells get 0 (the agent has no reason to jump back to them).
 * Frontier cells (any unvisited neighbour in 3×3) get amplified by
 * (1 + α) — the known/unknown boundary is where new information lives.
 *
 * Pure function of (belief). No env mutation.
 */
export function densityMap(
  belief: Belief,
  out: Float64Array,
  frontierAlpha: number = DEFAULT_FRONTIER_ALPHA,
): void {
  const grid = belief.grid;
  const bGold = belief.bGold;
  const visited = belief.visited;

  // Base density: bGold masked by visited.
  for (let i = 0; i < grid * grid; i++) {
    out[i] = visited[i] === 1 ? 0 : bGold[i];
  }

  // Frontier amplification: a cell is on the frontier if any 3×3 neighbour
  // is unvisited. Skip if α <= 0 (no frontier bonus).
  if (frontierAlpha <= 0) return;

  for (let r = 0; r < grid; r++) {
    for (let c = 0; c < grid; c++) {
      const idx = r * grid + c;
      if (visited[idx] === 1) continue;  // visited cells stay at 0
      let hasUnvisitedNeighbour = false;
      for (let dr = -1; dr <= 1 && !hasUnvisitedNeighbour; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
          if (visited[nr * grid + nc] === 0) {
            hasUnvisitedNeighbour = true;
            break;
          }
        }
      }
      if (hasUnvisitedNeighbour) {
        out[idx] *= (1 + frontierAlpha);
      }
    }
  }
}

/**
 * True if the straight queen-walk from (r, c) to (tr, tc) is clear of
 * stones. The queen walks one cell at a time along the dominant axis
 * (both axes for diagonals). Stops at the first stone or out-of-bounds.
 *
 * Reads the TRUE world cells (used by env.step), NOT the belief — this
 * is the same contract as `canMove` in environment.ts.
 */
export function queenPathWalkable(
  env: Env,
  r: number,
  c: number,
  tr: number,
  tc: number,
): boolean {
  const g = env.world.grid;
  const cells = env.world.cells;
  const STONE = 2;  // local constant — avoids circular import for the enum
  let cr = r;
  let cc = c;
  while (cr !== tr || cc !== tc) {
    const dr = tr === cr ? 0 : (tr > cr ? 1 : -1);
    const dc = tc === cc ? 0 : (tc > cc ? 1 : -1);
    const nr = cr + dr;
    const nc = cc + dc;
    if (nr < 0 || nr >= g || nc < 0 || nc >= g) return false;
    if (cells[nr * g + nc] === STONE) return false;
    cr = nr;
    cc = nc;
  }
  return true;
}

/**
 * Find the best queen-jump target along direction `dirIdx` from the
 * agent's current pose. Returns the (r, c) of the highest-density cell
 * whose straight queen-walk path is clear of stones, or null if no
 * valid target exists in that direction.
 *
 * The scan walks every cell in the forward half-plane defined by
 * DIRECTIONS[dirIdx] — i.e. cells where the dot product with the
 * direction is positive. For cardinal directions this is a forward
 * ray; for diagonals it's a forward quadrant.
 *
 * `minCheb` excludes cells too close to the current pose (no point in
 * a queen jump of Chebyshev distance 1 — that's just a length-1 leap).
 */
export function queenTarget(
  env: Env,
  dirIdx: number,
  densityBuf: Float64Array,
  minCheb: number = 2,
): [number, number] | null {
  const [dr, dc] = DIRECTIONS[dirIdx];
  const r = env.r;
  const c = env.c;
  const g = env.world.grid;
  densityMap(env.belief, densityBuf);

  let best = -1;
  let bestCell: [number, number] | null = null;

  for (let tr = 0; tr < g; tr++) {
    for (let tc = 0; tc < g; tc++) {
      const d = densityBuf[tr * g + tc];
      if (d <= 0) continue;
      // Forward cone: dot product with direction must be positive.
      // For cardinal directions this picks the forward half-plane.
      // For diagonals it picks the forward quadrant.
      const fr = tr - r;
      const fc = tc - c;
      if (dr !== 0 && Math.sign(fr) !== dr) continue;
      if (dc !== 0 && Math.sign(fc) !== dc) continue;
      const cheb = Math.max(Math.abs(fr), Math.abs(fc));
      if (cheb < minCheb) continue;
      if (!queenPathWalkable(env, r, c, tr, tc)) continue;
      if (d > best) {
        best = d;
        bestCell = [tr, tc];
      }
    }
  }
  return bestCell;
}
